import { useCallback, useState } from 'react';

import {
  AdminSectionCard,
  HelpAnchor,
  InlineError,
  Pill,
  SkeletonBlock,
  Switch,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { SEED_EDITOR_PAGE_FLOW_CONFIG } from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  editorPageFlowStatusLabel,
  nextEditorPageFlowConfig,
} from './EditorPageFlowScene.transform';

export function EditorPageFlowScene() {
  const [config, setConfig] = useState(SEED_EDITOR_PAGE_FLOW_CONFIG);
  const [loading] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleEnabled = useCallback(() => {
    setToggleBusy(true);
    setError(null);
    setConfig((prev) => nextEditorPageFlowConfig(prev));
    setToggleBusy(false);
  }, []);

  return (
    <AdminSceneShell
      sceneName="EditorPageFlowScene"
      title="Editor page flow"
      description="Enable the editor page-flow surface for projects."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-3"
    >
      <Commentable name="Panel">
        <div className="space-y-3">
          <AdminSectionCard
            title="Editor page flow"
            description="Controls whether the chapter editor uses Word-style A4 pagination or the legacy infinite-scroll layout. GA default is ON — use this toggle to opt out."
            actions={
              loading ? (
                <SkeletonBlock className="h-5 w-9 rounded-full" />
              ) : (
                <>
                  <Pill tone={config.enabled ? 'success' : 'default'}>
                    {editorPageFlowStatusLabel(config.enabled)}
                  </Pill>
                  <Switch
                    id="editor-page-flow-enabled"
                    checked={config.enabled}
                    onCheckedChange={toggleEnabled}
                    disabled={toggleBusy}
                    aria-label="Editor page flow"
                  />
                </>
              )
            }
          >
            {config.enabled ? (
              <p className="text-sm text-muted">
                <strong className="font-medium text-zinc-900 dark:text-zinc-100">
                  ON
                </strong>{' '}
                — Word-style A4 paginated editor, scoped under{' '}
                <code className="text-zinc-600 dark:text-zinc-300">data-page-flow</code>
                .
              </p>
            ) : (
              <p className="text-sm text-muted">
                <strong className="font-medium text-zinc-900 dark:text-zinc-100">
                  OFF
                </strong>{' '}
                — legacy infinite-scroll editor.
              </p>
            )}
          </AdminSectionCard>

          {error ? <InlineError>{error}</InlineError> : null}
        </div>
      </Commentable>
    </AdminSceneShell>
  );
}
